import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Option } from '@/component/ui/cascader/single'

/**
 * 生成唯一ID的计数器，按域名分组
 */
const idCounters = new Map<string, number>()

/**
 * 生成唯一ID
 * @param {string} domain 域名，不同域名下的ID独立计数
 * @returns {number} 唯一ID
 */
export function uniqueId(domain = 'global'): number {
  if (!idCounters.has(domain)) {
    idCounters.set(domain, 1)
    return 1
  } else {
    const id = idCounters.get(domain)! + 1
    idCounters.set(domain, id)
    return id
  }
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatNumber(
  value: number | undefined,
  fractionDigits = 2,
  options = [
    [100000000, '亿'],
    [10000, '万'],
  ],
) {
  if (!value && value !== 0) return '-'
  for (let i = 0; i < options.length; i += 1) {
    if (Math.abs(Number(value)) >= Number(options[i][0])) {
      return [
        (Math.floor((Number(value) / Number(options[i][0])) * 10 ** fractionDigits) / 10 ** fractionDigits).toLocaleString(),
        options[i][1],
      ].join('')
    }
  }
  return [(value ?? '-').toLocaleString(), null].join('')
}

/**
 *
 * @param {number} value
 * @param {number} maxFractionDigits 保留至多几位小数位数
 * @param {number} minFractionDigits 保留至少几位小数位数
 * @returns
 */
export function formatPrice(value: number, maxFractionDigits = 2, minFractionDigits = 2) {
  if (value === null || !isFinite(value)) return '-'
  const options = {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: minFractionDigits,
  }
  return (value / 100).toLocaleString('zh-CN', options)
}
export function formatFabricPrice(data: { priceMin?: string; priceMax?: string; priceUnit?: string }) {
  return `${formatPrice(+(data.priceMin || 0) * 100)}${
    data.priceMax !== data.priceMin ? `~${formatPrice(+(data.priceMax || 0) * 100)}` : ''
  }/${data.priceUnit}`
}

/**
 * 百分比转换
 * @param {number} value
 * @param {number} maxFractionDigits 保留至多几位小数位数
 * @param {number} minFractionDigits 保留至少几位小数位数
 * @returns
 */
export function formatPercent(value: number | undefined, maxFractionDigits = 2, minFractionDigits = 0) {
  if (!value || !isFinite(value)) return '-'
  return value.toLocaleString('zh-CN', {
    style: 'percent' as const,
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  })
}

/**
 * 给数字添加千分位分隔符
 * @param {number} value 需要格式化的数字
 * @param {number} fractionDigits 保留小数位数，默认为0
 * @returns {string} 格式化后的字符串
 */
export function formatThousands(value: number | undefined, fractionDigits = 0): string {
  if (!value && value !== 0) return '-'
  return value.toLocaleString('zh-CN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

export function formatDate(date: Date, format: string = 'YYYY-MM-DD HH:mm:ss') {
  const yyyy = date.getFullYear() + ''
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  const sec = String(date.getSeconds()).padStart(2, '0')

  const formattedDate = format
    .replace('YYYY', yyyy)
    .replace('MM', mm)
    .replace('DD', dd)
    .replace('HH', hh)
    .replace('mm', min)
    .replace('ss', sec)

  return formattedDate
}

/**
 * 根据时间规则格式化显示时间
 * @param {Date | Number | String} inputTime - 传入的时间，可以是Date对象或可解析的时间字符串
 * @param {string} type - 显示类型：'bubble'（会话框）或 'list'（会话列表）
 * @returns {string} 格式化后的时间字符串
 */
export function formatChatTime(inputTime: Date | number | string, type = 'bubble') {
  // 确保输入是Date对象
  const time = inputTime instanceof Date ? inputTime : new Date(inputTime)

  // 验证时间有效性
  if (isNaN(time.getTime())) {
    throw new Error('Invalid time input')
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  // 获取时间的各个部分
  const year = time.getFullYear()
  const month = time.getMonth() + 1
  const date = time.getDate()
  const hours = time.getHours().toString().padStart(2, '0')
  const minutes = time.getMinutes().toString().padStart(2, '0')
  const dayOfWeek = ['日', '一', '二', '三', '四', '五', '六'][time.getDay()]

  // 判断时间范围
  if (time >= today) {
    // 今日
    if (type === 'bubble') {
      return `${hours}:${minutes}`
    } else {
      return `${hours}:${minutes}`
    }
  } else if (time >= yesterday) {
    // 昨日
    if (type === 'bubble') {
      return `昨天 ${hours}:${minutes}`
    } else {
      return '昨天'
    }
  } else if (time >= sevenDaysAgo) {
    // 近7天（除去今日和昨日）
    if (type === 'bubble') {
      return `星期${dayOfWeek} ${hours}:${minutes}`
    } else {
      return `星期${dayOfWeek}`
    }
  } else if (time.getFullYear() === now.getFullYear()) {
    // 今年
    const monthStr = month.toString().padStart(2, '0')
    const dateStr = date.toString().padStart(2, '0')

    if (type === 'bubble') {
      return `${monthStr}-${dateStr} ${hours}:${minutes}`
    } else {
      return `${monthStr}-${dateStr}`
    }
  } else {
    // 往年
    const yearStr = year.toString()
    const monthStr = month.toString().padStart(2, '0')
    const dateStr = date.toString().padStart(2, '0')

    if (type === 'bubble') {
      return `${yearStr}-${monthStr}-${dateStr} ${hours}:${minutes}`
    } else {
      return `${yearStr}-${monthStr}-${dateStr}`
    }
  }
}

export function arraysEqual(arr1: unknown[], arr2: unknown[]) {
  if (arr1 === arr2) return true

  if (arr1.length !== arr2.length) return false

  const sortedArr1 = [...arr1].sort()
  const sortedArr2 = [...arr2].sort()

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false
    }
  }

  return true
}

// 节流函数
export function throttle(func: (...args: unknown[]) => void, delay: number) {
  let lastCall = 0
  let timeoutId: NodeJS.Timeout | null = null

  return function (...args: unknown[]) {
    const now = new Date().getTime()
    const later = () => {
      lastCall = now
      func.apply(this, args)
    }

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    if (now - lastCall < delay) {
      timeoutId = setTimeout(later, delay - (now - lastCall))
    } else {
      later()
    }
  }
}

export function mergeAndDeduplicate<T>(arr1: T[], arr2: T[], key: keyof T) {
  const map = new Map()

  // 遍历第一个数组
  for (const item of arr1) {
    map.set(item[key], item)
  }

  // 遍历第二个数组，如果id不存在则添加
  for (const item of arr2) {
    if (!map.has(item[key])) {
      map.set(item[key], item)
    }
  }

  return Array.from(map.values())
}

// 判断两个数组是否有交集
export function hasIntersection(arr1: unknown[], arr2: unknown[]) {
  const set1 = new Set(arr1)
  const set2 = new Set(arr2)

  for (const item of set1) {
    if (set2.has(item)) {
      return true
    }
  }

  return false
}

// 获取所有叶子节点的value
export function getAllLeafNodes(options: Option[]) {
  const leafNodes: string[] = []

  function traverse(nodes: Option[]) {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return
    }

    for (const node of nodes) {
      if (!node.children || node.children.length === 0) {
        leafNodes.push(node.value!)
      } else {
        traverse(node.children)
      }
    }
  }

  traverse(options)
  return leafNodes
}
